import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  FormLabel,
  TextField,
  IconButton,
  Box,
  Stack,
  Divider,
  Typography,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useGetAutoFillVariables } from '@/api/hooks/custom-payments/useGetAutoFillVariables';
import FieldSettingsErrors from './FieldSettingsErrors';
import FieldValidationSettings, {
  EMPTY_FORM_PROPERTIES_STATE,
  evaluateValueAgainstState,
  toFormProperties,
  toFormPropertiesState,
  validateFormPropertiesState,
} from './FieldValidationSettings';

const EMPTY_LABEL = { en: '', fr: '', es: '' };
const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
  { code: 'es', name: 'Español' },
];

const AutoFillSettingsModal = ({
  open,
  onClose,
  fieldTitle,
  value,
  formProperties,
  onSave,
}) => {
  const [type, setType] = useState('none');
  const [defaultValue, setDefaultValue] = useState('');
  const [options, setOptions] = useState([
    { value: '', label: { ...EMPTY_LABEL } },
  ]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [variable, setVariable] = useState('');
  const [validation, setValidation] = useState({
    ...EMPTY_FORM_PROPERTIES_STATE,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [conflictErrors, setConflictErrors] = useState([]);

  const {
    variables,
    loading: variablesLoading,
    error: variablesError,
  } = useGetAutoFillVariables(open && type === 'variable');

  useEffect(() => {
    if (!open) return;
    const emptyOptions = [{ value: '', label: { ...EMPTY_LABEL } }];
    if (value?.type === 'options') {
      setType('options');
      setOptions(
        value.options?.length
          ? value.options.map((opt) => ({
              ...opt,
              label: { ...EMPTY_LABEL, ...opt.label },
            }))
          : emptyOptions,
      );
      setDefaultValue('');
      setVariable('');
    } else if (value?.type === 'default') {
      setType('default');
      setDefaultValue(value.defaultValue ?? '');
      setOptions(emptyOptions);
      setVariable('');
    } else if (value?.type === 'variable') {
      setType('variable');
      setVariable(value.variable ?? '');
      setDefaultValue('');
      setOptions(emptyOptions);
    } else {
      setType('none');
      setDefaultValue('');
      setOptions(emptyOptions);
      setVariable('');
    }
    setValidation(toFormPropertiesState(formProperties));
    setValidationErrors({});
    setConflictErrors([]);
  }, [open, value, formProperties]);

  const handleValidationChange = (next) => {
    setValidation(next);
    setValidationErrors({});
    setConflictErrors([]);
  };

  const handleTypeChange = (nextType) => {
    setType(nextType);
    setConflictErrors([]);
  };

  const handleDefaultValueChange = (nextValue) => {
    setDefaultValue(nextValue);
    setConflictErrors([]);
  };

  const handleOptionValueChange = (index, newValue) => {
    setConflictErrors([]);
    setOptions((prev) =>
      prev.map((opt, i) => (i === index ? { ...opt, value: newValue } : opt)),
    );
  };

  const handleOptionLabelChange = (index, lang, newLabel) => {
    setOptions((prev) =>
      prev.map((opt, i) =>
        i === index
          ? { ...opt, label: { ...opt.label, [lang]: newLabel } }
          : opt,
      ),
    );
  };

  const handleAddOption = () => {
    setOptions((prev) => [...prev, { value: '', label: { ...EMPTY_LABEL } }]);
  };

  const handleRemoveOption = (index) => {
    setConflictErrors([]);
    setOptions((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [{ value: '', label: { ...EMPTY_LABEL } }];
    });
  };

  const handleSave = () => {
    const errors = validateFormPropertiesState(validation);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setConflictErrors([{ scope: 'formProperties' }]);
      return;
    }

    let nextAutoFill;
    const conflicts = [];
    if (type === 'none') {
      nextAutoFill = undefined;
    } else if (type === 'default') {
      const trimmed = defaultValue.trim();
      const [violation] = evaluateValueAgainstState(defaultValue, validation);
      if (violation) conflicts.push({ scope: 'defaultValue', violation });
      nextAutoFill =
        trimmed === '' ? undefined : { type: 'default', defaultValue };
    } else if (type === 'variable') {
      nextAutoFill = variable ? { type: 'variable', variable } : undefined;
    } else {
      const seen = new Set();
      const kept = [];
      options.forEach((opt, index) => {
        const optionValue = opt.value.trim();
        if (!optionValue || seen.has(optionValue)) return;
        seen.add(optionValue);
        kept.push({
          position: index + 1,
          option: { ...opt, value: optionValue },
        });
      });

      kept.forEach(({ position, option }) => {
        const [violation] = evaluateValueAgainstState(option.value, validation);
        if (violation) {
          conflicts.push({
            scope: 'option',
            position,
            value: option.value,
            violation,
          });
        }
      });

      nextAutoFill =
        kept.length === 0
          ? undefined
          : { type: 'options', options: kept.map((entry) => entry.option) };
    }

    if (conflicts.length > 0) {
      setConflictErrors(conflicts);
      return;
    }

    setConflictErrors([]);
    onSave({
      autoFill: nextAutoFill,
      formProperties: toFormProperties(validation),
    });
  };

  const handleClear = () => {
    onSave({ autoFill: undefined, formProperties: undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        Field Settings
        {fieldTitle && (
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {fieldTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Validation
        </Typography>
        <FieldValidationSettings
          value={validation}
          errors={validationErrors}
          onChange={handleValidationChange}
        />
        <Divider sx={{ my: 2 }} />
        <Typography variant='subtitle2' sx={{ mb: 1 }}>
          Auto-fill
        </Typography>
        <FormControl>
          <FormLabel>Auto-fill type</FormLabel>
          <RadioGroup
            row
            value={type}
            onChange={(e) => handleTypeChange(e.target.value)}>
            <FormControlLabel value='none' control={<Radio />} label='None' />
            <FormControlLabel
              value='default'
              control={<Radio />}
              label='Default'
            />
            <FormControlLabel
              value='options'
              control={<Radio />}
              label='Options'
            />
            <FormControlLabel
              value='variable'
              control={<Radio />}
              label='Variable'
            />
          </RadioGroup>
        </FormControl>

        {type !== 'none' && (
          <Box sx={{ mt: 2 }}>
            {type === 'default' ? (
              <TextField
                fullWidth
                size='small'
                label='Default value'
                value={defaultValue}
                onChange={(e) => handleDefaultValueChange(e.target.value)}
              />
            ) : type === 'variable' ? (
              <FormControl
                fullWidth
                size='small'
                error={Boolean(variablesError)}>
                <InputLabel id='autofill-variable-label'>Variable</InputLabel>
                <Select
                  labelId='autofill-variable-label'
                  label='Variable'
                  value={variable}
                  onChange={(e) => setVariable(e.target.value)}
                  disabled={variablesLoading}>
                  {variable && !variables.some((v) => v.key === variable) && (
                    <MenuItem value={variable}>{variable}</MenuItem>
                  )}
                  {variables.map((v) => (
                    <MenuItem key={v.key} value={v.key}>
                      {v.label}
                    </MenuItem>
                  ))}
                </Select>
                {variablesLoading && (
                  <FormHelperText>Loading variables…</FormHelperText>
                )}
                {variablesError && (
                  <FormHelperText>{variablesError}</FormHelperText>
                )}
              </FormControl>
            ) : (
              <Stack spacing={2}>
                <FormControl size='small' sx={{ maxWidth: 200 }}>
                  <InputLabel id='options-language-label'>
                    Label language
                  </InputLabel>
                  <Select
                    labelId='options-language-label'
                    label='Label language'
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}>
                    {LANGUAGES.map((lang) => (
                      <MenuItem key={lang.code} value={lang.code}>
                        {lang.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Stack spacing={1}>
                  {options.map((opt, index) => (
                    <Box key={index}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          sx={{ flex: 1 }}
                          size='small'
                          label='Value'
                          value={opt.value}
                          onChange={(e) =>
                            handleOptionValueChange(index, e.target.value)
                          }
                        />
                        <TextField
                          sx={{ flex: 1 }}
                          size='small'
                          label={`Label (${
                            LANGUAGES.find((l) => l.code === selectedLanguage)
                              ?.name
                          })`}
                          value={opt.label[selectedLanguage]}
                          onChange={(e) =>
                            handleOptionLabelChange(
                              index,
                              selectedLanguage,
                              e.target.value,
                            )
                          }
                        />
                        <IconButton
                          aria-label='Remove option'
                          size='small'
                          onClick={() => handleRemoveOption(index)}>
                          <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                  <Box>
                    <Button
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={handleAddOption}>
                      Add option
                    </Button>
                  </Box>
                </Stack>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <FieldSettingsErrors errors={conflictErrors} />
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Button
          color='error'
          onClick={handleClear}
          disabled={!value && !formProperties}>
          Clear settings
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleSave}
            sx={{ color: '#fff' }}>
            OK
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AutoFillSettingsModal;
